import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';

const defaultErrorNote = 'Could not retrieve data from cookiebox provider';

@Injectable()
export class CookieboxService {
    private cookieboxData: Map<string, SafeHtml> = new Map<string, SafeHtml>();

    public constructor(private readonly domSanitizer: DomSanitizer) {}

    public fetch(url: string, errorNote: string = defaultErrorNote): Observable<SafeHtml> {
        return this.toFetchPipe(url, errorNote);
    }

    public fetchMultiple(
        urls: Record<string, string>,
        errorNote: string = defaultErrorNote,
    ): Observable<Record<string, SafeHtml>> {
        const htmlsDict = {} as Record<string, Observable<SafeHtml>>;

        for (const key in urls) {
            if (Object.hasOwn(urls, key)) {
                htmlsDict[key] = this.toFetchPipe(urls[key], errorNote);
            }
        }

        return forkJoin(htmlsDict);
    }

    private toFetchPipe(url: string, errorNote: string = defaultErrorNote): Observable<SafeHtml> {
        const previouslyFetchedHtml = this.cookieboxData.get(url);

        if (previouslyFetchedHtml) {
            return of(previouslyFetchedHtml);
        }
        return fromFetch(url).pipe(
            switchMap((response: Response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    return throwError(() => new Error(errorNote));
                }
            }),
            map((data: { html: string }) => this.domSanitizer.sanitize(SecurityContext.HTML, data.html) as SafeHtml),
            tap((html: SafeHtml) => this.cookieboxData.set(url, html)),
        );
    }
}
